import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, Users as UsersIcon, Search, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { t } from "../lib/i18n";

export default function MessagingPage() {
    const { user } = useAuth();
    const [channels, setChannels] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [presets, setPresets] = useState([]);
    const [text, setText] = useState("");
    const [presetPick, setPresetPick] = useState(null);
    const [presetParams, setPresetParams] = useState({});
    const [showNewDm, setShowNewDm] = useState(false);
    const scrollRef = useRef(null);

    async function loadChannels() {
        try { const { data } = await api.get("/chat/channels"); setChannels(data); if (!activeId && data[0]) setActiveId(data[0].id); }
        catch (e) { toast.error(apiError(e)); }
    }
    useEffect(() => { api.get("/chat/presets").then(({ data }) => setPresets(data)).catch(() => {}); loadChannels(); }, []);

    const loadMessages = useCallback(async () => {
        if (!activeId) return;
        try {
            const { data } = await api.get("/chat/messages", { params: { channel_id: activeId } });
            setMessages(data);
            // mark read up to newest
            if (data.length) {
                await api.post("/chat/read", { channel_id: activeId, up_to: data[data.length - 1].sent_at });
                loadChannels();
            }
        } catch (e) { toast.error(apiError(e)); }
    }, [activeId]);
    useEffect(() => { loadMessages(); }, [loadMessages]);

    // Polling
    useEffect(() => {
        const t = setInterval(() => { loadMessages(); loadChannels(); }, 12000);
        return () => clearInterval(t);
    }, [loadMessages]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    async function send() {
        if (!activeId) return;
        const body = { channel_id: activeId };
        if (presetPick) {
            // Require every declared placeholder before sending; matches server 400.
            const missing = (presetPick.placeholders || []).filter((ph) => !String(presetParams[ph] || "").trim());
            if (missing.length) {
                toast.error(`${t(user, "chat_preset_params")}: ${missing.join(", ")}`);
                return;
            }
            body.preset_id = presetPick.id;
            body.params = presetParams;
        } else {
            if (!text.trim()) return;
            body.text = text;
        }
        try {
            await api.post("/chat/messages", body);
            setText(""); setPresetPick(null); setPresetParams({});
            loadMessages();
        } catch (e) { toast.error(apiError(e)); }
    }

    const activeChannel = channels.find((c) => c.id === activeId);
    const presetMissing = presetPick ? (presetPick.placeholders || []).filter((ph) => !String(presetParams[ph] || "").trim()) : [];
    const canSend = presetPick ? presetMissing.length === 0 : !!text.trim();

    return (
        <div className="mx-auto max-w-6xl px-0 sm:px-6 py-0 sm:py-8">
            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-0 md:gap-4 h-[calc(100vh-72px)] md:h-[80vh]">
                <div className="border-r md:border md:rounded-xl border-gray-200 bg-white overflow-y-auto md:fx-shadow-card" data-testid="chat-channels-list">
                    <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                        <div className="font-display font-semibold text-[#1E3355] text-sm flex items-center gap-1"><MessageSquare className="w-4 h-4" /> {t(user, "nav_messages")}</div>
                        <button onClick={() => setShowNewDm(true)} className="text-xs text-[#1E3355] font-semibold hover:underline" data-testid="chat-new-dm">{t(user, "chat_new_dm")}</button>
                    </div>
                    <ul className="divide-y divide-gray-100">
                        {channels.map((c) => (
                            <li key={c.id}>
                                <button onClick={() => setActiveId(c.id)} className={`w-full text-left p-3 hover:bg-gray-50 ${activeId === c.id ? "bg-[#F4F6F9]" : ""}`} data-testid={`channel-${c.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-semibold text-sm text-[#1E3355] truncate">{c.kind === "site" ? "# " : "@ "}{c.name}</span>
                                        {c.unread > 0 && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white bg-[#E02B2B]" data-testid={`unread-${c.id}`}>{c.unread}</span>}
                                    </div>
                                    <div className="text-[11px] text-gray-500 truncate mt-0.5">{c.last_preview || t(user, "chat_no_messages")}</div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="flex flex-col bg-white md:rounded-xl md:border border-gray-200 md:fx-shadow-card overflow-hidden">
                    {activeChannel && (
                        <div className="p-3 border-b border-gray-100">
                            <div className="font-display font-semibold text-[#1E3355]">{activeChannel.kind === "site" ? "# " : "@ "}{activeChannel.name}</div>
                        </div>
                    )}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
                        {messages.length === 0 && <div className="text-center text-sm text-gray-500 mt-10">{t(user, "chat_no_messages")}</div>}
                        {messages.map((m) => (
                            <div key={m.id} className={`flex ${m.sender_id === user?.id ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender_id === user?.id ? "bg-[#1E3355] text-white" : "bg-[#F4F6F9] text-gray-800"}`}>
                                    <div className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${m.sender_id === user?.id ? "text-white/70" : "text-gray-500"}`}>{m.sender_name}{m.preset_key && ` · [${m.preset_key}]`}</div>
                                    <div className="whitespace-pre-wrap">{m.rendered_text || m.text}</div>
                                    <div className={`text-[9px] mt-0.5 ${m.sender_id === user?.id ? "text-white/60" : "text-gray-400"}`}>{m.sent_at?.slice(11, 16)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-gray-100 p-3 space-y-2">
                        {presetPick ? (
                            <div className="rounded-md bg-amber-50 border border-amber-200 p-2">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800">Preset · {presetPick.key}</div>
                                    <button onClick={() => { setPresetPick(null); setPresetParams({}); }} className="text-amber-800 hover:text-red-700"><X className="w-3.5 h-3.5" /></button>
                                </div>
                                <div className="text-xs text-gray-700">{previewPreset(presetPick, presetParams, user?.language || "en")}</div>
                                {presetPick.placeholders?.length > 0 && (
                                    <div className="mt-2 space-y-1">
                                        {presetPick.placeholders.map((ph) => {
                                            const missing = !String(presetParams[ph] || "").trim();
                                            return (
                                                <input key={ph} placeholder={ph} value={presetParams[ph] || ""} onChange={(e) => setPresetParams({ ...presetParams, [ph]: e.target.value })} className={`w-full rounded border px-2 py-1 text-xs bg-white ${missing ? "border-[#E02B2B] focus:border-[#E02B2B] focus:ring-1 focus:ring-[#E02B2B]" : "border-amber-300"}`} data-testid={`preset-param-${ph}`} required />
                                            );
                                        })}
                                        {presetMissing.length > 0 && (
                                            <div className="text-[10px] text-[#E02B2B] font-semibold" data-testid="preset-params-missing">{t(user, "chat_preset_params")}: {presetMissing.join(", ")}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : null}
                        <div className="flex gap-2">
                            <select onChange={(e) => { const p = presets.find((x) => x.id === e.target.value); setPresetPick(p || null); setPresetParams({}); e.target.value = ""; }} className="text-xs rounded border border-gray-300 bg-white px-2 py-2" defaultValue="" data-testid="chat-preset-select">
                                <option value="">{t(user, "chat_pick_preset")}</option>
                                {presets.map((p) => <option key={p.id} value={p.id}>{`${p.key} — ${p.en.slice(0, 40)}`}</option>)}
                            </select>
                            <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={t(user, "chat_placeholder")} className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355]" disabled={!!presetPick} data-testid="chat-text-input" />
                            <button onClick={send} disabled={!canSend} className="inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-40 disabled:cursor-not-allowed" data-testid="chat-send"><Send className="w-4 h-4" /> {t(user, "chat_send")}</button>
                        </div>
                    </div>
                </div>
            </div>
            {showNewDm && <NewDmModal onClose={() => setShowNewDm(false)} onOpened={(chId) => { setShowNewDm(false); loadChannels(); setActiveId(chId); }} />}
        </div>
    );
}

function previewPreset(p, params, lang) {
    let s = p[lang] || p.en || "";
    Object.entries(params || {}).forEach(([k, v]) => { s = s.replaceAll(`{${k}}`, String(v || `{${k}}`)); });
    return s;
}

function NewDmModal({ onClose, onOpened }) {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const { user } = useAuth();
    useEffect(() => {
        // fallback: HO can list all; others may not have /users so try and gracefully degrade
        api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.id !== user.id))).catch(() => setUsers([]));
    }, [user.id]);
    async function open(peer) {
        try { const { data } = await api.post("/chat/dm/open", { peer_id: peer.id }); onOpened(data.channel_id); }
        catch (e) { toast.error(apiError(e)); }
    }
    const filtered = q ? users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())) : users;
    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="chat-new-dm-modal">
            <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="font-display font-semibold text-[#1E3355]">Start a DM</div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-3 border-b border-gray-100">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <ul className="flex-1 overflow-y-auto divide-y divide-gray-100">
                    {filtered.map((u) => (
                        <li key={u.id}>
                            <button onClick={() => open(u)} className="w-full text-left p-3 hover:bg-gray-50" data-testid={`dm-target-${u.id}`}>
                                <div className="font-semibold text-sm text-[#1E3355]">{u.name}</div>
                                <div className="text-[11px] text-gray-500">{u.email} · {u.role}</div>
                            </button>
                        </li>
                    ))}
                    {filtered.length === 0 && <li className="p-6 text-center text-sm text-gray-500">No users to DM.</li>}
                </ul>
            </div>
        </div>
    );
}
